import { useState } from 'react';
import { Bluetooth, Printer } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import type { Sale } from '@/api/endpoints';
import { Alert, Button, Modal } from '@/components/ui';
import { isAndroid, printDocument, printRoute } from '@/lib/printing';
import { saleDocument } from '@/lib/receiptDocuments';

/**
 * A printable receipt for one sale.
 *
 * It is rendered from the sale the API already returned rather than from a
 * separately generated document, so a reprint a week later says exactly what
 * the customer was handed: the sale's own lines, its own totals, its own
 * tenders. Nothing here recalculates anything.
 *
 * Printing goes to the driver running on the till, which speaks ESC/POS to the
 * thermal printer; a server-rendered PDF would add a round trip and a
 * dependency for a document that is 40 lines of text.
 */
export function SaleReceipt({ sale, onClose }: { sale: Sale; onClose: () => void }) {
  const format = useFormat();
  const context = useAppSelector((state) => state.tenant.context);
  const [printing, setPrinting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const organization = context?.organization;
  const settings = context?.settings;

  const document = (): ReturnType<typeof saleDocument> =>
    saleDocument(sale, organization ?? {}, format, {
      taxLabel: settings?.taxNumberLabel ?? 'Tax ID',
      footerNote: settings?.receiptFooter,
    });

  /**
   * Printed by whichever route this device is set to — the driver on a till,
   * RawBT on a phone, the browser dialog otherwise. A shop that has set none
   * of it up still has to be able to hand over a receipt.
   */
  const print = async (route?: 'rawbt'): Promise<void> => {
    setFailure(null);
    setPrinting(true);
    try {
      const result = await printDocument(document(), route);
      if (!result.printed) setFailure(result.error);
    } finally {
      setPrinting(false);
    }
  };

  // A till roll is 80mm wide; A4 is a page. Anything else falls back to the
  // roll, because that is what a shop without an answer actually owns.
  const narrow = (settings?.receiptTemplate ?? 'THERMAL_80') !== 'A4';

  const address = [organization?.addressLine1, organization?.addressLine2, organization?.city]
    .filter(Boolean)
    .join(', ');

  const tenders = sale.payments ?? [];
  const discount = Number(sale.discountAmount ?? '0');
  const tax = Number(sale.taxAmount ?? '0');
  const shipping = Number(sale.shippingAmount ?? '0');

  return (
    <Modal
      open
      onClose={onClose}
      size={narrow ? 'sm' : 'lg'}
      title={`Receipt ${sale.number}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {/* On a phone the Bluetooth printer is the one in the room, so it
              is offered here rather than only in settings. */}
          {isAndroid() && printRoute() === 'auto' && (
            <Button variant="outline" icon={Bluetooth} onClick={() => void print('rawbt')}>
              Bluetooth
            </Button>
          )}
          <Button icon={Printer} loading={printing} onClick={() => void print()}>Print</Button>
        </div>
      }
    >
      {failure && <div className="mb-3"><Alert tone="danger">{failure}</Alert></div>}

      <div
        id="receipt-print-area"
        className={`mx-auto bg-white p-4 font-mono text-[11px] leading-snug text-black ${
          narrow ? 'max-w-[80mm]' : 'max-w-full text-[12px]'
        }`}
      >
        <header className="text-center">
          <h2 className="text-sm font-bold uppercase">{organization?.name}</h2>
          {address && <p>{address}</p>}
          {/* How to reach them, which is what a customer coming back with a
              receipt is usually looking for. Both come from the settings. */}
          {(organization?.phone || organization?.email) && (
            <p>{[organization.phone, organization.email].filter(Boolean).join(' · ')}</p>
          )}
          {organization?.taxNumber && (
            <p>{settings?.taxNumberLabel ?? 'Tax ID'}: {organization.taxNumber}</p>
          )}
          {settings?.receiptHeader && <p className="mt-1">{settings.receiptHeader}</p>}
        </header>

        <hr className="my-2 border-dashed border-black" />

        <dl className="grid grid-cols-[auto_1fr] gap-x-2">
          <dt>Receipt</dt>
          <dd className="text-right">{sale.number}</dd>
          <dt>Date</dt>
          <dd className="text-right">{formatDateTime(sale.issuedAt ?? sale.createdAt, format)}</dd>
          {sale.branch && (
            <>
              <dt>Branch</dt>
              <dd className="text-right">{sale.branch.name}</dd>
            </>
          )}
          <dt>Customer</dt>
          <dd className="text-right">{sale.customer?.name ?? 'Walk-in'}</dd>
        </dl>

        <hr className="my-2 border-dashed border-black" />

        {/* Four columns cannot share 32 characters: the name gets its own
            line, and the arithmetic sits under it. */}
        {sale.items?.map((item) => (
          <div key={item.id} className="mb-1">
            <p>{item.description || item.variant.product.name}</p>
            <p className="text-[10px]">{item.variant.sku}</p>
            <div className="flex justify-between gap-2 tabular">
              <span>
                {formatQuantity(item.quantity, format)} × {formatMoney(item.unitPrice, format)}
              </span>
              <span>{formatMoney(item.lineTotal, format)}</span>
            </div>
          </div>
        ))}

        <hr className="my-2 border-dashed border-black" />

        <dl className="grid grid-cols-[1fr_auto] gap-x-2">
          <dt>Subtotal</dt>
          <dd className="text-right tabular">{formatMoney(sale.subtotal, format)}</dd>

          {discount > 0 && (
            <>
              <dt>Discount</dt>
              <dd className="text-right tabular">−{formatMoney(sale.discountAmount, format)}</dd>
            </>
          )}
          {tax > 0 && (
            <>
              <dt>Tax</dt>
              <dd className="text-right tabular">{formatMoney(sale.taxAmount, format)}</dd>
            </>
          )}
          {shipping > 0 && (
            <>
              <dt>Shipping</dt>
              <dd className="text-right tabular">{formatMoney(sale.shippingAmount, format)}</dd>
            </>
          )}

          <dt className="border-t border-black pt-1 text-sm font-bold">Total</dt>
          <dd className="border-t border-black pt-1 text-right text-sm font-bold tabular">
            {formatMoney(sale.total, format)}
          </dd>
        </dl>

        {tenders.length > 0 && (
          <>
            <hr className="my-2 border-dashed border-black" />
            <dl className="grid grid-cols-[1fr_auto] gap-x-2">
              {tenders.map((tender) => (
                <div key={tender.id} className="contents">
                  <dt>{tender.payment.method.name}</dt>
                  <dd className="text-right tabular">{formatMoney(tender.amount, format)}</dd>
                </div>
              ))}
            </dl>
          </>
        )}

        {Number(sale.dueAmount) > 0 && (
          <p className="mt-2 font-bold">
            Balance due: {formatMoney(sale.dueAmount, format)}
          </p>
        )}

        <hr className="my-2 border-dashed border-black" />

        <footer className="text-center">
          {settings?.receiptFooter ? (
            <p>{settings.receiptFooter}</p>
          ) : (
            <p>Thank you for your business.</p>
          )}
          {/* A reprint must say so, or a duplicate can be passed off as a second sale. */}
          <p className="mt-1 text-[10px]">Printed {formatDateTime(new Date().toISOString(), format)}</p>
        </footer>
      </div>
    </Modal>
  );
}
